import { inject, Injectable, Injector } from '@angular/core';
import { Store } from '@ngxs/store';
import { Navigate } from '@ngxs/router-plugin';

import {
  ClearFormModel,
  ClearTaskFromState,
  FormSaved,
  GetTaskDetails,
  MarkFormInvalid,
  MarkFormValid,
  SetFormErrorFields,
  SetFormModel,
  SetFormModelDirty,
  SetFormValues,
  SetFormWarningFields,
  SetOutcomesExemptFromValidation,
  SetOutcomesToDisable,
  TaskCompleted,
  WorkflowStarted,
} from './eben.actions';
import { EbenState } from './eben.state';
import { Observable, of, throwError } from 'rxjs';
import { PROCESS_ENDED_URL, TASK_NOT_FOUND_IN_TIME_URL, WORKFLOW_NOT_FOUND_URL } from '../routing-constants';
import { FormFieldModel, FormModel, FormOutcomeModel, FormService, FormValues, WidgetVisibilityService } from '@alfresco/adf-core';
import { EbenService } from './eben.service';
import {
  DialogCancelProcessConfirmModel,
  EbenDialogResultModel,
  EbenTaskDetail,
  FormFieldWarning,
  ProcessHasBeenEndedError,
  ProcessHasNoTaskInTimeError,
  ProcessInstanceCancelError,
  ProcessInstanceNotFoundError,
} from '@alf-nx-workspace/eben/interfaces';
import { TaskQueryRequestRepresentationModel } from '@alfresco/adf-process-services';
import { catchError, debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import { BpmService, SnackbarService } from '@alf-nx-workspace/shared/utils';
import { MatDialog } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/portal';

@Injectable({
  providedIn: 'root',
})
export class EbenFacade {

  readonly injector = inject(Injector);
  readonly store = inject(Store);
  readonly ebenService = inject(EbenService);
  readonly matDialog = inject(MatDialog);
  readonly snackbarService = inject(SnackbarService)
  readonly formService = inject(FormService);
  readonly bpmService = inject(BpmService);

  public readonly formErrorFields$: Observable<FormFieldModel[]> = this.store.select(EbenState.formErrorFields);
  public readonly formWarningFields$: Observable<FormFieldWarning[]> = this.store
    .select(EbenState.formWarningFields)
    .pipe(filter((warnings: FormFieldWarning[]) => !!warnings));
  public readonly isFormDirty$: Observable<boolean> = this.store.select(EbenState.isFormDirty);
  private visibilityService: WidgetVisibilityService;
  public readonly form$: Observable<FormModel> = this.store.select(EbenState.form).pipe(
    debounceTime(50),
    map((form: FormModel) => {
      this.visibilityService.refreshVisibility(form);
      return form;
    })
  );

  constructor() {
    this.visibilityService = this.injector.get(WidgetVisibilityService);
  }


  public getTaskDetails(taskId: string): Observable<EbenTaskDetail> {
    return this.store.dispatch(new GetTaskDetails(taskId))
      .pipe(
        switchMap(() => this.store.select(EbenState.getTaskDetails(taskId)),
        ));
  }

  public getTaskFormVariables(taskId: string) {
    return this.visibilityService.getTaskProcessVariable(taskId);
  }

  public getTaskForm(taskId: string): Observable<FormModel> {
    return this.ebenService.formService.getTaskForm(taskId);
  }

  public getTotalTasks(requestNode: TaskQueryRequestRepresentationModel) {
    return this.ebenService.taskListService.getTotalTasks(requestNode);
  }

  public setFormModel(form: FormModel): void {
    this.store.dispatch(new SetFormValues(null, form.values));
    this.store.dispatch(new SetFormModel(form.json));
  }

  public setFormValues(fieldId: string, values: FormValues): void {
    this.store.dispatch(new SetFormValues(fieldId, {...values}));
  }

  public setFormErrorFields(errorsField: FormFieldModel[]) {
    this.store.dispatch(new SetFormErrorFields(errorsField.map((field: FormFieldModel) => field.json)));
  }

  public setFormFieldWarnings(warningsFields: FormFieldWarning[]) {
    this.store.dispatch(new SetFormWarningFields(warningsFields));
  }

  public setFormModelDirty(dirty = false): void {
    this.store.dispatch(new SetFormModelDirty(dirty));
  }

  public workflowStarted(): void {
    this.store.dispatch(new WorkflowStarted());
  }

  public completeTask(): void {
    this.store.dispatch(new TaskCompleted());
  }

  public saveForm(form: FormModel): void {
    this.store.dispatch(new FormSaved(form));
  }

  public clearFormState(): void {
    this.store.dispatch(new ClearFormModel());
  }

  public setOutcomesExemptFromValidation(outcomes: string[]): void {
    this.store.dispatch(new SetOutcomesExemptFromValidation(outcomes));
  }

  public setOutcomesToDisable(outcomes: string[]): void {
    this.store.dispatch(new SetOutcomesToDisable(outcomes));
  }

  public isOutcomeDisabled(outcome: FormOutcomeModel) {
    return this.store.select(EbenState.isOutcomeDisabled(outcome));
  }

  public clearDialogOutlet(): Observable<EbenState> {
    return this.store.dispatch(new Navigate([{outlets: {dialog: null}}], null, {queryParamsHandling: 'preserve'}));
  }

  public scrollToFormField(field: FormFieldModel): void {
    if (field) {
      const scrollTo: HTMLElement = document.documentElement.querySelector(`#field-${field.id}-container`);
      if (scrollTo) {
        const className = scrollTo.className;
        scrollTo.scrollIntoView({behavior: 'smooth'});
        scrollTo.className = scrollTo.className + ' blink';
        setTimeout(() => {
          scrollTo.className = className;
        }, 1500);
      }
    }
  }

  public canCancelWorkflow(initiatorId: string, userId: string, processDefinitionKey?: string): boolean {
    const processDefinitionWhitelist = ['generische-freigabe'];
    if(processDefinitionKey && !processDefinitionWhitelist.includes(processDefinitionKey)) {
      return false;
    }
    return initiatorId === userId;
  }

  public openCancelWorkflowConfirmationDialog(processInstanceId: string, enforceWhitelistedProcessDefinition: boolean = false, component: ComponentType<unknown>): Observable<DialogCancelProcessConfirmModel> {
    return this.matDialog
      .open(component)
      .afterClosed()
      .pipe(
        switchMap((result: DialogCancelProcessConfirmModel) => {
          if (!result?.confirm) {
            return of(result);
          }
          return this.bpmService.cancelProcessInstanceAsUser(processInstanceId, enforceWhitelistedProcessDefinition).
          pipe(
            tap(() => this.snackbarService.onProcessInstanceCancelSuccess()),
            map(() => result),
            catchError((error) => {
              this.snackbarService.error(new ProcessInstanceCancelError(error, processInstanceId), true);
              return throwError(() => error);
            })
          );
        })
      )
  }

  public markFormInvalid(fieldId: string): void {
    this.store.dispatch(new MarkFormInvalid(fieldId));
  }

  public markFormValid(fieldId: string): void {
    this.store.dispatch(new MarkFormValid(fieldId));
  }

  public handleRedirectError(error: unknown): EbenDialogResultModel {
    const dialogResult: EbenDialogResultModel = {
      triggerReload: false,
      redirect: ""
    };

    if (error instanceof ProcessInstanceNotFoundError) {
      dialogResult.redirect = WORKFLOW_NOT_FOUND_URL.replace(':processInstanceId', error.processInstanceId);
    } else if (error instanceof ProcessHasBeenEndedError) {
      dialogResult.triggerReload = true;
      dialogResult.redirect = PROCESS_ENDED_URL.replace(':processInstanceId', error.processInstance.id);
    } else if (error instanceof ProcessHasNoTaskInTimeError) {
      dialogResult.redirect = TASK_NOT_FOUND_IN_TIME_URL.replace(':processInstanceId', error.processInstance.id);
    }

    return dialogResult;
  }

  public navigateToOutlet(dialogPath: string, skipLocationChange = false): void {
    this.store.dispatch(new Navigate([{outlets: {dialog: dialogPath}}], null, {queryParamsHandling: 'preserve', skipLocationChange}));
  }

  public clearTaskFromState(taskId: string): void {
    this.store.dispatch(new ClearTaskFromState(taskId));
  }

}
